/**
 * Investment entity types matching backend schemas
 * Source: backend/app/schemas/investments.py, backend/app/domain/investments/enums.py
 */

import type { DocumentType } from "@/domain/documentEnums";
import type { InvestmentDocumentResponse } from "@/domain/documentBlocks";
import type { BeneficiaryPrimaryContact, FiscalQuotaState } from "@/domain/beneficiary";
import type { TaxIncentiveTypeValue } from "@/domain/classification/constants";
import type { InvestorStatus, InvestorType } from "@/domain/investor";
import type { ProjectSubtype } from "@/domain/project";
import type { ContractType } from "@/domain/contract";

/**
 * Investment state enum - represents current state of investment
 * Source: backend/app/domain/investments/enums.py
 */
export const InvestmentState = {
  PROSPECT: "prospect",
  RESERVED: "reserved",
  FINANCING: "financing",
  FUNDED: "funded",
  DECLARED: "declared",
  CANCELLED: "cancelled",
  EXPIRED: "expired",
  REJECTED: "rejected",
} as const;
export type InvestmentState = (typeof InvestmentState)[keyof typeof InvestmentState];

/**
 * Investment event types - actions/events that occur during lifecycle
 * Source: backend/app/domain/investments/enums.py
 */
export const InvestmentEventType = {
  CREATED: "created",
  PROPOSAL_SENT: "proposal_sent",
  RESERVATION_CONTRACT_SENT: "reservation_contract_sent",
  FINANCING_CONTRACT_SENT: "financing_contract_sent",
  COMMUNICATION_CONTRACT_SENT: "communication_contract_sent",
  CONTRACT_SIGNED: "contract_signed",
  CONTRACT_VOIDED: "contract_voided",
  CONTRACT_DECLINED: "contract_declined",
  CONTRACT_DELIVERY_FAILED: "contract_delivery_failed",
  INVOICE_SENT: "invoice_sent",
  RESERVATION_CREATED: "reservation_created",
  RESERVATION_EXTENDED: "reservation_extended",
  RESERVATION_EXPIRY_SET: "reservation_expiry_set",
  RESERVATION_RELEASED: "reservation_released",
  RESERVATION_EXPIRED: "reservation_expired",
  QUOTA_RESERVED: "quota_reserved",
  QUOTA_RELEASED: "quota_released",
  QUOTA_EXPIRED: "quota_expired",
  PAYMENT_RECEIVED: "payment_received",
  PAYMENT_CONFIRMED: "payment_confirmed",
  PAYMENT_REVERSED: "payment_reversed",
  TAX_SUBMITTED: "tax_submitted",
  TAX_ACCEPTED: "tax_accepted",
  TAX_REJECTED: "tax_rejected",
  DOCUMENT_UPLOADED: "document_uploaded",
  DOCUMENT_DOWNLOADED: "document_downloaded",
  DOCUMENT_APPROVED: "document_approved",
  DOCUMENT_REJECTED: "document_rejected",
  STATE_CHANGED: "state_changed",
  AMOUNT_UPDATED: "amount_updated",
  NOTES_UPDATED: "notes_updated",
  BENEFICIARY_CHANGED: "beneficiary_changed",
  RETURN_PERCENTAGE_UPDATED: "return_percentage_updated",
  CANCELLED: "cancelled",
  REJECTED: "rejected",
} as const;
export type InvestmentEventType = (typeof InvestmentEventType)[keyof typeof InvestmentEventType];

/**
 * Investment document types - specific to investment lifecycle
 * Source: backend/app/domain/investments/enums.py
 */
export enum InvestmentDocumentType {
  INVESTMENT_PROPOSAL = "investment_proposal",
  RESERVATION_CONTRACT = "reservation_contract",
  FINANCING_CONTRACT = "financing_contract",
  COMMUNICATION_CONTRACT = "communication_contract",
  PAYMENT_RECEIPT = "payment_receipt",
  TAX_SUBMISSION_CERTIFICATE = "tax_submission_certificate",
}

/**
 * Re-export the canonical DocumentStatus from documentEnums to avoid divergence.
 */
export { DocumentStatus } from "../documentEnums";

/**
 * Base fields shared by all investment types (Hub and regular)
 * Used to avoid duplication between HubInvestment and InvestmentResponse
 */
export interface InvestmentBaseFields {
  uuid: string;
  amount: string; // Decimal with 2 decimal places
  return_percentage: string; // Decimal with 2 decimal places
  currency: string;
}

/**
 * Pagination parameters shared across investment queries
 */
export interface InvestmentPaginationParams {
  limit?: number;
  offset?: number;
}

/**
 * Summary model for investor data in investment responses
 * Source: backend/app/schemas/investors.py - InvestorSummary
 */
interface InvestorSummary {
  uuid: string;
  type: "individual" | "company";
  status: InvestorStatus;
  display_name: string;
  email: string | null;
  tax_identifier: string | null;
  city: string | null;
  phone?: string | null;
}

/**
 * Summary model for beneficiary data in investment responses
 * Source: backend/app/schemas/beneficiaries.py - BeneficiarySummary
 */
interface BeneficiarySummary {
  uuid: string;
  name: string;
  tax_id: string;
  email: string | null;
  phone: string | null;
  incentive_type: "film_audiovisual" | "performing_arts_music" | null;
  primary_contact?: BeneficiaryPrimaryContact | null;
}

/**
 * Minimal project snapshot attached to a quota — used to build the display
 * label in Investment UIs ("Cuota 2024 · <project>" for film, "Cuota 2024" or
 * "Cuota 2024 · N proyectos" for music).
 * Source: backend/app/schemas/beneficiaries.py - LinkedProjectSummaryResponse
 */
interface LinkedProjectSummary {
  uuid: string;
  title: string;
  subtype: ProjectSubtype | null;
  city: string | null;
  production_start_date: string | null;
  production_end_date: string | null;
}

/**
 * Summary model for fiscal quota data
 * Source: backend/app/schemas/beneficiaries.py - FiscalQuotaSummary
 */
interface FiscalQuotaSummary {
  uuid: string;
  fiscal_year: number;
  state: FiscalQuotaState;
  // The parts of the quota's name, never the text: the front composes it.
  incentive_type: TaxIncentiveTypeValue | null;
  estimated_deduction: string;
  reserved_amount: string;
  completed_amount: string;
  utilization_percentage: number;
  linked_projects: LinkedProjectSummary[];
  cost_breakdown: Record<string, string> | null;
  total_budget: string | null;
  total_eligible_cost: string | null;
  deduction_base: string | null;
  total_generated_deduction: string | null;
  own_contribution: string | null;
  subsidies: Array<Record<string, unknown>> | null;
}

/**
 * Summary model for user data in investment events
 * Source: backend/app/schemas/users.py - UserSummary
 */
interface UserSummary {
  uuid: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  full_name: string;
  profile_picture_signed_url?: string | null;
}

/**
 * What an investment is made of, whoever its investor is
 * Source: backend/app/schemas/investments.py - InvestmentTermsRequest
 */
export interface InvestmentTermsRequest {
  fiscal_quota_uuid: string; // UUID of the fiscal quota
  amount: string; // Decimal string, must be > 0
  return_percentage: string; // Decimal string, must be > 0
  state?: InvestmentState; // Initial state, defaults to PROSPECT
}

/**
 * Request model for creating a new investment
 * Source: backend/app/schemas/investments.py - InvestmentCreateRequest
 */
export interface InvestmentCreateRequest extends InvestmentTermsRequest {
  investor_uuid: string; // UUID of the investor
}

export interface AllowedInvestmentTransition {
  state: InvestmentState;
  missing_documents?: DocumentType[];
}

/**
 * Request model for updating investment via PATCH
 * Source: backend/app/schemas/investments.py - InvestmentUpdateRequest
 */
export interface InvestmentUpdateRequest {
  amount?: string; // New investment amount (Decimal string, > 0)
  return_percentage?: string; // New return percentage (Decimal string, > 0)
  fiscal_quota_uuid?: string; // New fiscal quota UUID; the promoter moves with it
  state?: InvestmentState; // New state
  rejection_reason?: string;
  void_contracts?: boolean; // Void the contracts that hold the current terms and apply the change with them
}

/**
 * Request for extending reservation
 * Source: backend/app/schemas/investment_actions.py - ExtendReservationRequest
 */
export interface ExtendReservationRequest {
  new_expires_at: string; // ISO datetime string - must be in the future
}

/**
 * Response model for investment summary data
 * Source: backend/app/schemas/investments.py - InvestmentResponse
 */
export interface InvestmentResponse extends InvestmentBaseFields {
  deduction: string;
  state: InvestmentState;
  allowed_transitions?: AllowedInvestmentTransition[];
  reservation_expires_at?: string | null; // ISO datetime string
  notes?: string | null;
  created_at?: string | null; // ISO datetime string
  updated_at?: string | null; // ISO datetime string

  // Rich summary objects instead of UUIDs
  investor: InvestorSummary;
  beneficiary: BeneficiarySummary;
  quota: FiscalQuotaSummary;
}

export interface InvestmentWarning {
  code: "investor_music_cap_exceeded" | (string & {});
  context: Record<string, string | number>;
}

export interface InvestmentWriteResponse extends InvestmentResponse {
  warnings: InvestmentWarning[];
}

/**
 * Response model for investment event data
 * Source: backend/app/schemas/investments.py - InvestmentEventResponse
 */
export interface InvestmentEventResponse {
  uuid: string;
  event_type: InvestmentEventType;
  occurred_at: string; // ISO datetime string
  user?: UserSummary | null;
  metadata?: Record<string, unknown> | null;
  created_at: string; // ISO datetime string
}

/**
 * Response model for investment with all nested details
 * Source: backend/app/schemas/investments.py - InvestmentWithDetailsResponse
 */
export interface InvestmentWithDetailsResponse {
  investment: InvestmentResponse;
  events: InvestmentEventResponse[];
  documents: InvestmentDocumentResponse[];
  binding: InvestmentBinding;
}

/**
 * How far a contract that holds the investment's terms went: sent or signed
 * through the platform, or uploaded already signed
 * Source: backend/app/domain/investments/terms.py - BoundContractStatus
 */
export type BoundContractStatus = "sent" | "signed" | "uploaded";

/**
 * A contract sent or signed with the investment's current terms
 * Source: backend/app/schemas/investments.py - BoundContractResponse
 */
export interface BoundContract {
  document_uuid: string;
  contract_type: ContractType;
  status: BoundContractStatus;
}

/**
 * What the investment's current terms are committed to
 * Source: backend/app/schemas/investments.py - InvestmentBindingResponse
 */
export interface InvestmentBinding {
  payment_registered: boolean;
  contracts: BoundContract[];
}

/**
 * Response for extending reservation
 * Source: backend/app/schemas/investment_actions.py - ExtendReservationResponse
 */
export interface ExtendReservationResponse {
  extension_granted: boolean;
  new_expires_at: string; // ISO datetime string
  extended_at: string; // ISO datetime string
}

/**
 * Query parameters for listing investments
 * Source: backend/app/api/routes/investments.py - list_investments
 */
export type InvestmentSortField =
  | "amount"
  | "beneficiary_name"
  | "created_at"
  | "investor_name"
  | "return_percentage"
  | "state";

export interface InvestmentSort {
  key: InvestmentSortField;
  direction: "asc" | "desc";
}

interface InvestmentFilterParams {
  state?: InvestmentState | InvestmentState[]; // Filter by state (single or multi-value)
  investor_uuid?: string; // Filter by investor UUID
  beneficiary_uuid?: string; // Filter by beneficiary UUID
  fiscal_quota_uuid?: string; // Filter by fiscal quota UUID (exact match)
  search?: string; // Search in investor, beneficiary, notes and the #INV- reference
  amount_min?: string; // Minimum investment amount
  amount_max?: string; // Maximum investment amount
  investor_type?: InvestorType | InvestorType[]; // Filter by investor type
  fiscal_year?: string | number; // Filter by fiscal year (from fiscal quota)
  sort?: InvestmentSort[]; // Multi-key sort, wins over sort_by/sort_direction
  sort_by?: InvestmentSortField;
}

export interface InvestmentQueryParams extends InvestmentFilterParams {
  sort_direction?: "asc" | "desc"; // Sort direction
  limit?: number; // Number of results to return (1-500, default 100)
  offset?: number; // Number of results to skip (>= 0, default 0)
}

export interface InvestmentExportParams extends InvestmentFilterParams {
  sort_direction?: "asc" | "desc" | null;
}

/**
 * Paginated investment list response
 * Source: backend/app/schemas/investments.py - InvestmentListResponse
 */
export interface InvestmentPagination {
  investments: InvestmentWithDetailsResponse[];
  total_count: number;
  total_unfiltered_count: number;
  limit: number;
  offset: number;
}

/**
 * Callback contract for investment row-action menus.
 *
 * Each handler corresponds to a user action available in the investment
 * context menu.  Consumers wire these up to modals / navigation / mutations.
 */
export interface InvestmentActionHandlers {
  onViewDetails: (investment: InvestmentWithDetailsResponse) => void;
  onEditInvestment?: (investment: InvestmentWithDetailsResponse) => void;
  onSendReservationContract?: (investment: InvestmentWithDetailsResponse) => void;
  onSendFinancingContract?: (investment: InvestmentWithDetailsResponse) => void;
  onSendCommunicationContract?: (investment: InvestmentWithDetailsResponse) => void;
  onExtendReservation?: (investment: InvestmentWithDetailsResponse) => void;
  onRejectInvestment?: (investment: InvestmentWithDetailsResponse) => void;
  onDeleteInvestment?: (investment: InvestmentWithDetailsResponse) => void;
  onDownloadAeat?: (investment: InvestmentWithDetailsResponse) => void;
  onUploadTaxCertificate?: (investment: InvestmentWithDetailsResponse) => void;
}
